import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Chip, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { styles } from "./UserInputForm.styles";
import { FONT_SIZES } from "../../constants/fontSizes";

const UserInputWithChip = ({
  label,
  value = [], // chip values
  inputBoxValue = "", // input box value
  handleInputBoxChange,
  handleChipChange,
  onClearAll,
  disabled,
  tooltipText,
  name,
  helperText,
  labelStyle,
  helperTextStyle,
}) => {
  const [currentValue, setCurrentValue] = useState(inputBoxValue);

  // Update local state when inputBoxValue prop changes
  useEffect(() => {
    setCurrentValue(inputBoxValue);
  }, [inputBoxValue]);

  // Notify parent of input box changes ----- the problem
  useEffect(() => {
    handleInputBoxChange(currentValue);
  }, [currentValue]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const trimmedValues = currentValue.trim().split(",");

      if (trimmedValues.length > 0) {
        const existingValues = Array.isArray(value) ? value : [];
        const valueSet = new Set([
          ...existingValues.map((val) => val.trim()),
          ...trimmedValues.map((val) => val.trim()),
        ]);

        handleChipChange(Array.from(valueSet).filter(Boolean));
        setCurrentValue("");
      }
    }
  };

  const handleDelete = (valueToDelete) => {
    const newValues = value.filter((val) => val !== valueToDelete);
    handleChipChange(newValues);
  };

  return (
    <Box sx={{ overflow: "visible" }}>
      <Tooltip
        title={tooltipText}
        arrow
        placement="right"
        enterDelay={200}
        leaveDelay={0}
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: FONT_SIZES.TEXT_HINT.xs,
              "@media (min-width: 600px)": {
                fontSize: FONT_SIZES.TEXT_HINT.sm,
              },
            },
          },
        }}
      >
        <TextField
          fullWidth
          label={label}
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          helperText={helperText}
          size="small"
          name={name}
          variant="standard"
          sx={{ ...styles.textField }}
          InputProps={{
            endAdornment: <InfoIcon color="action" sx={styles.infoIcon} />,
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Tooltip>
      {value.length > 0 && (
        <>
          <Button
            onClick={onClearAll}
            disabled={disabled}
            sx={styles.clearButton}
          >
            Clear All
          </Button>
          <CopyAllButton value={value} disabled={disabled} />
        </>
      )}
      <Box sx={styles.chipContainer}>
        {value.map((item, index) => (
          <Chip
            key={index}
            label={item}
            onDelete={() => handleDelete(item)}
            disabled={disabled}
            sx={styles.chip}
          />
        ))}
      </Box>
    </Box>
  );
};

export default UserInputWithChip;

// Add CopyAllButton component
const CopyAllButton = ({ value, disabled }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value.join(", "));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Optionally handle error
    }
  };

  return (
    <Button onClick={handleCopy} disabled={disabled} sx={styles.clearButton}>
      {copied ? "Copied!" : "Copy All"}
    </Button>
  );
};
